(()=>{
  const SELECTOR='[data-infinite-carousel]';
  const REDUCED=window.matchMedia('(prefers-reduced-motion: reduce)');

  function disableNativeDrag(viewport){
    viewport.addEventListener('dragstart',(event)=>event.preventDefault());
    viewport.querySelectorAll('img').forEach((image)=>{image.draggable=false;});
  }

  function init(root){
    if(root.dataset.carouselInitialized==='true')return;
    const viewport=root.querySelector('[data-carousel-viewport]');
    const track=root.querySelector('[data-carousel-track]');
    const prev=root.querySelector('[data-carousel-prev]');
    const next=root.querySelector('[data-carousel-next]');
    if(!viewport||!track)return;
    root.dataset.carouselInitialized='true';

    let animating=false;
    let timer=null;
    let pointerStartX=null;
    let pointerCurrentX=null;
    let activePointerId=null;
    let pointerCaptured=false;
    let dragging=false;
    let suppressClick=false;
    let userPaused=false;
    const autoplay=root.dataset.autoplay==='true'&&!REDUCED.matches;
    const interval=Math.max(Number(root.dataset.interval)||5000,3000);
    const items=()=>Array.from(track.children);
    const gap=()=>parseFloat(getComputedStyle(track).columnGap||getComputedStyle(track).gap||0);
    const step=()=>{const first=track.firstElementChild;return first?first.getBoundingClientRect().width+gap():0;};
    const canLoop=()=>{const children=items();if(children.length<2)return false;return track.scrollWidth>viewport.clientWidth+2;};
    const setTransition=(enabled)=>{track.style.transition=enabled?'transform .38s cubic-bezier(.22,.61,.36,1)':'none';};
    const reset=()=>{setTransition(false);track.style.transform='translate3d(0,0,0)';};
    const updateControls=()=>{const active=canLoop();[prev,next].forEach((button)=>{if(button){button.hidden=!active;button.disabled=!active;}});if(!active)stop();};

    function moveNext(){
      if(animating||!canLoop())return;
      const distance=step();
      if(!distance)return;
      animating=true;
      setTransition(true);
      track.style.transform=`translate3d(-${distance}px,0,0)`;
      track.addEventListener('transitionend',()=>{
        const first=track.firstElementChild;
        if(first)track.appendChild(first);
        reset();
        animating=false;
      },{once:true});
    }

    function movePrev(){
      if(animating||!canLoop())return;
      const last=track.lastElementChild;
      if(!last)return;
      track.insertBefore(last,track.firstElementChild);
      const distance=step();
      animating=true;
      setTransition(false);
      track.style.transform=`translate3d(-${distance}px,0,0)`;
      track.getBoundingClientRect();
      requestAnimationFrame(()=>{setTransition(true);track.style.transform='translate3d(0,0,0)';});
      track.addEventListener('transitionend',()=>{reset();animating=false;},{once:true});
    }

    function stop(){if(timer)window.clearInterval(timer);timer=null;}
    function start(){stop();if(autoplay&&!userPaused&&canLoop())timer=window.setInterval(moveNext,interval);}
    function clearPointer(){activePointerId=null;pointerStartX=null;pointerCurrentX=null;pointerCaptured=false;dragging=false;viewport.classList.remove('is-dragging');viewport.style.cursor=canLoop()?'grab':'';}
    function endDrag(event){
      if(activePointerId===null||event.pointerId!==activePointerId)return;
      const releasedId=activePointerId;
      const distance=(pointerCurrentX??event.clientX)-pointerStartX;
      const threshold=Math.min(70,Math.max(34,viewport.clientWidth*.055));
      if(dragging){
        suppressClick=true;
        window.setTimeout(()=>{suppressClick=false;},180);
      }
      if(dragging&&Math.abs(distance)>threshold){
        if(distance<0)moveNext();else movePrev();
      }else reset();
      if(pointerCaptured){try{viewport.releasePointerCapture(releasedId);}catch(error){}}
      clearPointer();
      start();
    }

    prev?.addEventListener('click',()=>{movePrev();start();});
    next?.addEventListener('click',()=>{moveNext();start();});
    root.addEventListener('mouseenter',stop);
    root.addEventListener('mouseleave',()=>{if(activePointerId===null)start();});
    root.addEventListener('focusin',stop);
    root.addEventListener('focusout',start);
    viewport.addEventListener('pointerdown',(event)=>{
      if(!canLoop()||event.button!==0||event.isPrimary===false)return;
      if(event.target.closest('button,input,select,textarea,label'))return;
      activePointerId=event.pointerId;
      pointerStartX=event.clientX;
      pointerCurrentX=event.clientX;
      pointerCaptured=false;
      dragging=false;
      stop();
      viewport.style.cursor='grab';
    },{passive:true});
    viewport.addEventListener('pointermove',(event)=>{
      if(activePointerId===null||event.pointerId!==activePointerId||pointerStartX===null)return;
      pointerCurrentX=event.clientX;
      const distance=pointerCurrentX-pointerStartX;
      if(Math.abs(distance)>8&&!dragging){
        dragging=true;
        viewport.classList.add('is-dragging');
        viewport.style.cursor='grabbing';
        viewport.setPointerCapture?.(event.pointerId);
        pointerCaptured=viewport.hasPointerCapture?.(event.pointerId)===true;
      }
      if(dragging&&!animating){
        setTransition(false);
        const maxDrag=step()*.7;
        const translated=Math.max(-maxDrag,Math.min(maxDrag,distance));
        track.style.transform=`translate3d(${translated}px,0,0)`;
      }
    },{passive:true});
    viewport.addEventListener('pointerup',endDrag,{passive:true});
    viewport.addEventListener('pointercancel',endDrag,{passive:true});
    viewport.addEventListener('click',(event)=>{if(!suppressClick)return;event.preventDefault();event.stopPropagation();},{capture:true});
    viewport.style.touchAction='pan-y';
    viewport.style.cursor=canLoop()?'grab':'';
    viewport.style.userSelect='none';
    disableNativeDrag(viewport);
    const observer=new ResizeObserver(()=>{reset();updateControls();viewport.style.cursor=canLoop()?'grab':'';start();});
    observer.observe(viewport);
    updateControls();
    start();
    root.addEventListener('shopify:block:select',()=>{userPaused=true;stop();});
    root.addEventListener('shopify:block:deselect',()=>{userPaused=false;start();});
  }

  function initHero(slider){
    if(slider.dataset.heroControllerInitialized==='true')return;
    const viewport=slider.querySelector('.hero-slider__viewport');
    const slides=[...slider.querySelectorAll('[data-hero-slide]')];
    const dots=[...slider.querySelectorAll('[data-hero-dot]')];
    const prev=slider.querySelector('[data-hero-prev]');
    const next=slider.querySelector('[data-hero-next]');
    if(!viewport||slides.length<2)return;

    slider.dataset.heroControllerInitialized='true';
    slider.dataset.initialized='true';

    let index=Math.max(0,slides.findIndex((slide)=>slide.classList.contains('is-active')));
    let timer=null;
    let pointerId=null;
    let pointerCaptured=false;
    let startX=0;
    let currentX=0;
    let dragging=false;
    let suppressClick=false;
    let animating=false;
    let currentSlide=null;
    let neighborSlide=null;
    let direction=0;
    const autoplay=slider.dataset.autoplay==='true'&&!REDUCED.matches;
    const interval=Math.max(Number(slider.dataset.interval)||5000,3000);
    const width=()=>viewport.getBoundingClientRect().width;
    const normalized=(value)=>(value+slides.length)%slides.length;

    function updateState(nextIndex){
      index=normalized(nextIndex);
      slides.forEach((slide,i)=>{
        const active=i===index;
        slide.classList.toggle('is-active',active);
        slide.setAttribute('aria-hidden',active?'false':'true');
      });
      dots.forEach((dot,i)=>{
        const active=i===index;
        dot.classList.toggle('is-active',active);
        dot.setAttribute('aria-current',active?'true':'false');
      });
    }

    function stop(){if(timer)window.clearInterval(timer);timer=null;}
    function start(){stop();if(autoplay&&!animating&&pointerId===null)timer=window.setInterval(()=>goTo(index+1),interval);}

    function clearInline(){
      slides.forEach((slide)=>{
        slide.style.removeProperty('transition');
        slide.style.removeProperty('transform');
        slide.style.removeProperty('z-index');
        slide.style.removeProperty('visibility');
        slide.style.removeProperty('opacity');
        slide.style.removeProperty('pointer-events');
      });
      currentSlide=null;
      neighborSlide=null;
      direction=0;
    }

    function preparePair(dir){
      currentSlide=slides[index];
      neighborSlide=slides[normalized(index+dir)];
      direction=dir;
      const w=width();
      [currentSlide,neighborSlide].forEach((slide)=>{
        slide.style.transition='none';
        slide.style.visibility='visible';
        slide.style.opacity='1';
        slide.style.pointerEvents='none';
      });
      currentSlide.style.zIndex='3';
      neighborSlide.style.zIndex='2';
      currentSlide.style.transform='translate3d(0,0,0)';
      neighborSlide.style.transform=`translate3d(${dir*w}px,0,0)`;
    }

    function renderDrag(delta){
      if(animating)return;
      const dir=delta<0?1:-1;
      if(!currentSlide||!neighborSlide||direction!==dir){
        clearInline();
        preparePair(dir);
      }
      const w=width();
      const limited=Math.max(-w,Math.min(w,delta));
      currentSlide.style.transform=`translate3d(${limited}px,0,0)`;
      neighborSlide.style.transform=`translate3d(${dir*w+limited}px,0,0)`;
    }

    function completeTransition(targetIndex,dir,fromDrag){
      if(animating)return;
      animating=true;
      stop();
      if(!currentSlide||!neighborSlide||direction!==dir)preparePair(dir);
      const w=width();
      [currentSlide,neighborSlide].forEach((slide)=>{slide.style.transition='transform .34s cubic-bezier(.22,.61,.36,1)';});
      requestAnimationFrame(()=>{
        currentSlide.style.transform=`translate3d(${-dir*w}px,0,0)`;
        neighborSlide.style.transform='translate3d(0,0,0)';
      });
      window.setTimeout(()=>{
        updateState(targetIndex);
        clearInline();
        animating=false;
        if(fromDrag){suppressClick=true;window.setTimeout(()=>{suppressClick=false;},160);}
        start();
      },350);
    }

    function snapBack(){
      if(!currentSlide||!neighborSlide){clearInline();return;}
      animating=true;
      const w=width();
      const dir=direction||1;
      [currentSlide,neighborSlide].forEach((slide)=>{slide.style.transition='transform .26s cubic-bezier(.22,.61,.36,1)';});
      requestAnimationFrame(()=>{
        currentSlide.style.transform='translate3d(0,0,0)';
        neighborSlide.style.transform=`translate3d(${dir*w}px,0,0)`;
      });
      window.setTimeout(()=>{clearInline();animating=false;start();},270);
    }

    function goTo(targetIndex){
      if(animating||pointerId!==null)return;
      const normalizedTarget=normalized(targetIndex);
      if(normalizedTarget===index)return;
      const forward=normalizedTarget===normalized(index+1);
      const backward=normalizedTarget===normalized(index-1);
      if(forward)completeTransition(normalizedTarget,1,false);
      else if(backward)completeTransition(normalizedTarget,-1,false);
      else{
        stop();
        updateState(normalizedTarget);
        start();
      }
    }

    prev?.addEventListener('click',()=>goTo(index-1));
    next?.addEventListener('click',()=>goTo(index+1));
    dots.forEach((dot,i)=>dot.addEventListener('click',()=>goTo(i)));

    viewport.style.touchAction='pan-y';
    viewport.style.cursor='grab';
    viewport.style.userSelect='none';
    disableNativeDrag(viewport);

    viewport.addEventListener('pointerdown',(event)=>{
      if(animating||event.button!==0||event.isPrimary===false)return;
      if(event.target.closest('button,.button,input,select,textarea,label'))return;
      pointerId=event.pointerId;
      pointerCaptured=false;
      startX=currentX=event.clientX;
      dragging=false;
      stop();
      viewport.style.cursor='grab';
    },{passive:true});

    viewport.addEventListener('pointermove',(event)=>{
      if(pointerId===null||event.pointerId!==pointerId)return;
      currentX=event.clientX;
      const delta=currentX-startX;
      if(Math.abs(delta)>8&&!dragging){
        dragging=true;
        viewport.classList.add('is-dragging');
        viewport.style.cursor='grabbing';
        viewport.setPointerCapture?.(event.pointerId);
        pointerCaptured=viewport.hasPointerCapture?.(event.pointerId)===true;
      }
      if(dragging)renderDrag(delta);
    },{passive:true});

    function releasePointer(event,cancelled=false){
      if(pointerId===null||event.pointerId!==pointerId)return;
      const releasedId=pointerId;
      const delta=(currentX||event.clientX)-startX;
      pointerId=null;
      viewport.classList.remove('is-dragging');
      viewport.style.cursor='grab';
      if(pointerCaptured){try{viewport.releasePointerCapture(releasedId);}catch(error){}}
      pointerCaptured=false;
      if(!dragging){clearInline();start();return;}
      dragging=false;
      const threshold=Math.min(120,Math.max(48,width()*.07));
      if(cancelled||Math.abs(delta)<=threshold){snapBack();return;}
      const dir=delta<0?1:-1;
      completeTransition(normalized(index+dir),dir,true);
    }

    viewport.addEventListener('pointerup',(event)=>releasePointer(event,false),{passive:true});
    viewport.addEventListener('pointercancel',(event)=>releasePointer(event,true),{passive:true});
    viewport.addEventListener('click',(event)=>{if(!suppressClick)return;event.preventDefault();event.stopPropagation();},{capture:true});
    slider.addEventListener('mouseenter',stop);
    slider.addEventListener('mouseleave',()=>{if(pointerId===null&&!animating)start();});
    slider.addEventListener('focusin',stop);
    slider.addEventListener('focusout',start);
    window.addEventListener('resize',()=>{if(pointerId!==null||animating){clearInline();pointerId=null;pointerCaptured=false;dragging=false;animating=false;updateState(index);}start();},{passive:true});
    slider.addEventListener('shopify:block:select',stop);
    slider.addEventListener('shopify:block:deselect',start);
    updateState(index);
    start();
  }

  function initAll(scope=document){
    scope.querySelectorAll(SELECTOR).forEach(init);
    scope.querySelectorAll('[data-hero-slider]').forEach(initHero);
  }

  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>initAll());
  else initAll();
  document.addEventListener('shopify:section:load',(event)=>initAll(event.target));
  document.addEventListener('elc:carousel:refresh',(event)=>initAll(event.detail?.scope||event.target||document));
})();
